import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const CapricornCard = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for CAPRICORN
  const staticMeta = {
    title: "AstroBhagya – Free Capricorn Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Capricorn yearly horoscope predictions 2026? Discover free Capricorn astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Capricorn yearly horoscope predictions 2026, Capricorn yearly Astrology predictions, Capricorn annual predictions, Yearly horoscope by Capricorn zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchCapricornYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `capricorn_yearly_${dateStr}`; // 🔥 CAPRICORN cache key
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find CAPRICORN data (id: 39 or sunShine contains "CAPRICORN") 
        const capricornData = data.find(
          item => item.id === 39 || item.sunShine?.toUpperCase().includes("CAPRICORN")
        );

        if (capricornData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, capricornData);
          localStorage.setItem(cacheKey, JSON.stringify(capricornData));
          setHoroscopeData(capricornData);
        } else {
          throw new Error("Capricorn data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Capricorn yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Capricorn Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchCapricornYearly(retries - 1);
      } else {
        // FALLBACK DATA - CAPRICORN 2026 
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Capricorn 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 combines Capricorn ambition with thoughtful planning. Patience and calculated action build lasting success.</p>
            <h2>Love and Relationships</h2>
            <p>Relationships deepen through mutual growth. Emotional maturity strengthens lasting bonds.</p>
            <h2>Career and Finances</h2>
            <p>Strategic achievements reward discipline. Long-term financial planning ensures stability.</p>
            <h2>Health & Wellness</h2>
            <p>Balanced routine prevents burnout. Yoga and meditation maintain focus.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Garnet/Black Onyx:</strong> Strengthens focus and stability.</li>
              <li><strong>Structured Routine:</strong> Balances work and self-care.</li>
              <li><strong>Saturday Donations:</strong> Dark items remove obstacles.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCapricornYearly();
  }, [fetchCapricornYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Capricorn 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchCapricornYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Capricorn 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Capricornnn.webp")}
                alt="Capricorn"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/sagittarius" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Sagittarius Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/aquarius" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Aquarius Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Capricorn Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Capricorn?",
                  answer: "Capricorn individuals are known for their ambition, discipline, practicality, and strong work ethic. Ruled by Saturn, they excel in long-term planning and responsibility.",
                },
                {
                  question: "2. What can Capricorn expect in love and relationships in 2026?",
                  answer: "2026 focuses on mutual growth and emotional maturity. Trust and shared goals strengthen lasting partnerships.",
                },
                {
                  question: "3. What does 2026 look like for Capricorn career growth?",
                  answer: "Strategic achievements reward dedication. Leadership opportunities through disciplined planning.",
                },
                {
                  question: "4. What health advice for Capricorn in 2026?",
                  answer: "Balanced routine prevents burnout. Regular exercise and meditation maintain productivity.",
                },
                {
                  question: "5. Key astrological remedies for Capricorn?",
                  answer: "Garnet/Black Onyx, structured routine, and Saturday donations enhance stability.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CapricornCard;   
